import React, { useState, useEffect, useCallback } from "react";
import { Card, Table, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import { FaEye, FaEdit, FaTrash, FaSearch, FaDownload, FaArrowLeft } from "react-icons/fa";
import { toast } from "react-toastify";

const InventoryDataViewer = ({ dataType, onBack }) => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Mock data - replace with actual API calls
      const mockData = {
        "Stock In": [
          { id: 1, item: "Rice", quantity: 100, cost_price: 50, supplier: "Supplier A", date: "2025-01-15", notes: "Good quality rice" },
          { id: 2, item: "Chicken", quantity: 50, cost_price: 200, supplier: "Supplier B", date: "2025-01-14", notes: "Fresh chicken" },
          { id: 3, item: "Oil", quantity: 25, cost_price: 120, supplier: "Supplier A", date: "2025-01-13", notes: "Cooking oil" }
        ],
        "Stock Out": [
          { id: 1, item: "Rice", quantity: 20, reason: "Sale", date: "2025-01-15", notes: "Sold to customer" },
          { id: 2, item: "Chicken", quantity: 10, reason: "Wastage", date: "2025-01-14", notes: "Expired items" },
          { id: 3, item: "Oil", quantity: 5, reason: "Transfer", date: "2025-01-13", notes: "Transferred to branch" }
        ],
        "Items": [
          { id: 1, name: "Rice", category: "Grains", unit: "KG", current_stock: 80, min_stock: 10, brand: "Local Brand" },
          { id: 2, name: "Chicken", category: "Meat", unit: "KG", current_stock: 40, min_stock: 5, brand: "Premium Brand" },
          { id: 3, name: "Oil", category: "Cooking", unit: "LTR", current_stock: 20, min_stock: 5, brand: "Brand A" }
        ],
        "Brands": [
          { id: 1, name: "Local Brand", description: "Local supplier brand", contact_person: "John Doe", contact_phone: "123-456-7890" },
          { id: 2, name: "Premium Brand", description: "High quality brand", contact_person: "Jane Smith", contact_phone: "098-765-4321" },
          { id: 3, name: "Brand A", description: "Standard quality brand", contact_person: "Mike Johnson", contact_phone: "555-123-4567" }
        ],
        "Pricing": [
          { id: 1, item: "Rice", cost_price: 50, selling_price: 70, markup: "40%", tax: "5%", effective_date: "2025-01-01" },
          { id: 2, item: "Chicken", cost_price: 200, selling_price: 280, markup: "40%", tax: "12%", effective_date: "2025-01-01" },
          { id: 3, item: "Oil", cost_price: 120, selling_price: 160, markup: "33%", tax: "18%", effective_date: "2025-01-01" }
        ]
      };
      
      setTimeout(() => {
        setTableData(mockData[dataType] || []);
        setLoading(false);
      }, 500);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
      toast.error("Error loading data");
    }
  }, [dataType]);

  useEffect(() => {
    if (dataType) {
      loadData();
    }
  }, [dataType, loadData]);

  useEffect(() => {
    // Filter data based on search term
    if (searchTerm) {
      const filtered = tableData.filter(item =>
        Object.values(item).some(value =>
          value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(tableData);
    }
  }, [searchTerm, tableData]);

  const handleEdit = (id) => {
    toast.info(`Edit functionality for ${dataType} ID: ${id}`);
  };

  const handleDelete = (id) => {
    if (window.confirm(`Are you sure you want to delete this ${dataType.toLowerCase()} record?`)) {
      setTableData(prev => prev.filter(item => item.id !== id));
      toast.success(`${dataType} record deleted successfully`);
    }
  };

  const handleExport = () => {
    toast.success(`Exporting ${dataType} data...`);
    // Add export functionality here
  };

  const getStatusBadge = (item) => {
    if (dataType === "Items" && item.current_stock <= item.min_stock) {
      return <span className="badge bg-danger">Low Stock</span>;
    }
    return <span className="badge bg-success">Active</span>;
  };

  return (
    <Card className="border-0 shadow-sm">
      <Card.Header className="bg-secondary text-white d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <FaEye className="me-2" />
          <h5 className="mb-0">View {dataType} Data</h5>
        </div>
        <div className="d-flex gap-2">
          <Button variant="light" size="sm" onClick={handleExport}>
            <FaDownload className="me-1" />
            Export
          </Button>
          {onBack && (
            <Button variant="light" size="sm" onClick={onBack}>
              <FaArrowLeft className="me-1" />
              Back
            </Button>
          )}
        </div>
      </Card.Header>
      <Card.Body className="p-4">
        {/* Search and Filter */}
        <Row className="mb-4">
          <Col md={6}>
            <Form.Group>
              <div className="position-relative">
                <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
                <Form.Control
                  type="text"
                  placeholder={`Search ${dataType.toLowerCase()}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="ps-5 form-control-lg"
                />
              </div>
            </Form.Group>
          </Col>
          <Col md={6}>
            <div className="d-flex justify-content-end align-items-center">
              <span className="text-muted me-3">
                Total Records: <strong>{filteredData.length}</strong>
              </span>
              <Button variant="outline-primary" onClick={loadData}>
                Refresh
              </Button>
            </div>
          </Col>
        </Row>

        {/* Data Table */}
        {loading ? (
          <div className="text-center p-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading {dataType.toLowerCase()} data...</p>
          </div>
        ) : (
          <div className="table-responsive">
            <Table striped bordered hover className="mb-0">
              <thead className="table-dark">
                <tr>
                  {filteredData.length > 0 && Object.keys(filteredData[0]).map((key) => (
                    <th key={key} className="text-center">
                      {key.charAt(0).toUpperCase() + key.slice(1).replace('_', ' ')}
                    </th>
                  ))}
                  <th className="text-center">Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row, index) => (
                  <tr key={index}>
                    {Object.values(row).map((value, idx) => (
                      <td key={idx} className="text-center">
                        {typeof value === 'number' && value % 1 !== 0 ? 
                          value.toFixed(2) : value}
                      </td>
                    ))}
                    <td className="text-center">
                      {getStatusBadge(row)}
                    </td>
                    <td className="text-center">
                      <div className="btn-group" role="group">
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          onClick={() => handleEdit(row.id)}
                          title="Edit"
                        >
                          <FaEdit />
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm" 
                          onClick={() => handleDelete(row.id)}
                          title="Delete"
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {filteredData.length === 0 && !loading && (
              <div className="text-center p-5">
                <p className="text-muted">No {dataType.toLowerCase()} data available</p>
                <Button variant="primary" onClick={loadData}>
                  Load Data
                </Button>
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default InventoryDataViewer;
